import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthState } from '../components/AuthView';

const STORAGE_KEY = 'maestro_theme';
const SCALE_KEY = 'maestro_display_scale';
const API_URL = 'https://api.maestroaudio.net';

const VALID_THEMES = ['light', 'dark', 'high-contrast'];
const VALID_SCALES = [0.85, 1.0, 1.15, 1.3];

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
  displayScale: 1.0,
  setDisplayScale: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function applyThemeClass(theme) {
  document.body.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast');
  document.body.classList.add(`theme-${theme}`);
}

function applyScale(scale) {
  document.documentElement.style.zoom = scale;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    let initial = 'light';
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (VALID_THEMES.includes(stored)) initial = stored;
    } catch {}
    applyThemeClass(initial);
    return initial;
  });

  const [displayScale, setDisplayScaleState] = useState(() => {
    let initial = 1.0;
    try {
      const stored = parseFloat(localStorage.getItem(SCALE_KEY));
      if (VALID_SCALES.includes(stored)) initial = stored;
    } catch {}
    applyScale(initial);
    return initial;
  });

  useEffect(() => { applyThemeClass(theme); }, [theme]);
  useEffect(() => { applyScale(displayScale); }, [displayScale]);

  const setTheme = useCallback((newTheme) => {
    if (!VALID_THEMES.includes(newTheme)) return;
    setThemeState(newTheme);
    try { localStorage.setItem(STORAGE_KEY, newTheme); } catch {}
    const token = getAuthState()?.accessToken;
    if (token) {
      fetch(`${API_URL}/api/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ theme: newTheme }),
      }).catch(() => {});
    }
  }, []);

  const setDisplayScale = useCallback((scale) => {
    const val = parseFloat(scale);
    if (!VALID_SCALES.includes(val)) return;
    setDisplayScaleState(val);
    try { localStorage.setItem(SCALE_KEY, String(val)); } catch {}
    const token = getAuthState()?.accessToken;
    if (token) {
      fetch(`${API_URL}/api/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ displayScale: val }),
      }).catch(() => {});
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, displayScale, setDisplayScale }}>
      {children}
    </ThemeContext.Provider>
  );
}

export async function loadThemeFromPreferences() {
  const token = getAuthState()?.accessToken;
  if (!token) return null;
  try {
    const locallySet = localStorage.getItem(STORAGE_KEY);
    if (VALID_THEMES.includes(locallySet)) return locallySet;

    const res = await fetch(`${API_URL}/api/user/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      localStorage.setItem(STORAGE_KEY, 'light');
      applyThemeClass('light');
      return 'light';
    }
    const data = await res.json();
    const theme = data?.theme;
    if (VALID_THEMES.includes(theme)) {
      localStorage.setItem(STORAGE_KEY, theme);
      applyThemeClass(theme);
      return theme;
    }
    if (data?.displayScale && VALID_SCALES.includes(parseFloat(data.displayScale))) {
      localStorage.setItem(SCALE_KEY, String(data.displayScale));
      applyScale(parseFloat(data.displayScale));
    }
  } catch {}
  localStorage.setItem(STORAGE_KEY, 'light');
  applyThemeClass('light');
  return 'light';
}
